import type { ResolvedGuide, ResolveGuidesResponse } from './types';

/**
 * Wrapper sobre `GET /v1/guides/resolve`. Nunca lanza: cualquier error de
 * red o de parsing devuelve un array vacío, para que el plugin no rompa
 * la app del cliente.
 *
 * Importante: el endpoint usa `?pageUrl=...` (no `?url=...`) — el backend
 * valida con class-validator `IsUrl({ require_protocol: true })`.
 */
export class GuideResolverClient {
  constructor(
    private readonly apiUrl: string,
    private readonly apiKey: string,
    private readonly debug = false,
  ) {}

  async resolve(endUserId: string, pageUrl: string): Promise<ResolvedGuide[]> {
    const params = new URLSearchParams({ endUserId, pageUrl });
    let response: Response;
    try {
      response = await fetch(`${this.apiUrl}/v1/guides/resolve?${params.toString()}`, {
        method: 'GET',
        headers: { 'X-Api-Key': this.apiKey },
        credentials: 'omit',
      });
    } catch (err) {
      if (this.debug) console.error('[veo] guides resolve fetch failed:', err);
      return [];
    }

    if (!response.ok) {
      if (this.debug) console.warn('[veo] guides resolve HTTP', response.status);
      return [];
    }

    try {
      const data = (await response.json()) as ResolveGuidesResponse;
      return Array.isArray(data.guides) ? data.guides : [];
    } catch (err) {
      if (this.debug) console.error('[veo] guides resolve parse failed:', err);
      return [];
    }
  }

  /**
   * Trae una guía específica por ID, sin pasar por la lógica de
   * activación. La usa el controller para resumir un walkthrough entre
   * páginas: el `resolve` no devuelve la guía si la URL actual no
   * matchea, pero el walkthrough activo debe poder consultarla igual.
   *
   * El endpoint backend (`GET /v1/guides/:guideId`) devuelve `GuideResponse`,
   * un superset de `ResolvedGuide` con campos de audit. Aquí descartamos los
   * extra y nos quedamos con el shape que el resto del SDK ya consume.
   *
   * Devuelve `null` en cualquier error (404, red, parse) — el caller debe
   * tratarlo como "guía ya no existe" y cancelar el walkthrough limpiamente.
   */
  async fetchById(guideId: string): Promise<ResolvedGuide | null> {
    let response: Response;
    try {
      response = await fetch(`${this.apiUrl}/v1/guides/${encodeURIComponent(guideId)}`, {
        method: 'GET',
        headers: { 'X-Api-Key': this.apiKey },
        credentials: 'omit',
      });
    } catch (err) {
      if (this.debug) console.error('[veo] guides fetchById network failed:', err);
      return null;
    }

    if (!response.ok) {
      if (this.debug) console.warn('[veo] guides fetchById HTTP', response.status);
      return null;
    }

    try {
      const data = (await response.json()) as Partial<ResolvedGuide>;
      if (
        typeof data.guideId !== 'string' ||
        typeof data.guideName !== 'string' ||
        typeof data.guideType !== 'string' ||
        !Array.isArray(data.guideSteps) ||
        !data.activationRules ||
        typeof data.displayFrequency !== 'string' ||
        typeof data.displayPriority !== 'number'
      ) {
        if (this.debug) console.warn('[veo] guides fetchById: unexpected shape');
        return null;
      }
      return {
        guideId: data.guideId,
        guideName: data.guideName,
        guideType: data.guideType,
        guideSteps: data.guideSteps,
        activationRules: data.activationRules,
        displayFrequency: data.displayFrequency,
        displayPriority: data.displayPriority,
      };
    } catch (err) {
      if (this.debug) console.error('[veo] guides fetchById parse failed:', err);
      return null;
    }
  }
}
